import { useMemo } from 'react';

import { useLists, useListsByIds } from '../../../../hooks/queries';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useParsedIdsFromStrings } from '../../../../hooks/useParsedIdsFromStrings';
import { dedupeBy } from '../../../../utils/dedupeBy';

type UseListOptionsProps = {
  search: string;
  page?: number;
  values: string[] | null;
  excludeDynamicLists?: boolean;
};

export const useListOptions = ({
  search,
  page = 0,
  values,
  excludeDynamicLists = false,
}: UseListOptionsProps) => {
  const { t } = useTranslation();
  const {
    listsCount,
    lists: suggestedLists,
    didError: didSuggestedListsError,
    isLoading: isSuggestedListsLoading,
  } = useLists({ search, page, excludeDynamicLists });
  const selectedIds = useParsedIdsFromStrings(values ?? []);
  const {
    lists: selectedLists,
    didError: didSelectedListsError,
    isLoading: isSelectedListsLoading,
  } = useListsByIds(selectedIds);

  const isLoading = isSelectedListsLoading || isSuggestedListsLoading;
  const didError = didSelectedListsError || didSuggestedListsError;

  const options = useMemo(() => {
    // Only show selected lists in "first" page of options
    const lists =
      page === 0
        ? dedupeBy([...selectedLists, ...suggestedLists], 'id')
        : suggestedLists.filter(({ id }) => !selectedIds.includes(id));
    return lists.map((list) => ({
      title: list.name,
      subtitle: t('v2.conditionBuilder.listSubtitle', { id: list.id }),
      value: list.id.toString(),
    }));
  }, [selectedLists, suggestedLists, t, page, selectedIds]);

  return { options, didError, isLoading, listsCount };
};
